import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { apiJson, apiPostJson, setApiOrganizationId } from "../lib/api";
import { useAuth } from "./AuthContext";

export type Organization = {
  id: number;
  name: string;
  owner_user_id: number;
  created_at: string;
  role?: string;
};

type OrganizationContextValue = {
  orgs: Organization[];
  activeOrg: Organization | null;
  activeOrgId: number | null;
  loading: boolean;
  setActiveOrgId: (id: number | null) => void;
  createOrg: (name: string) => Promise<Organization>;
  refreshOrgs: () => Promise<Organization[]>;
};

const STORAGE_KEY = "activeOrganizationId";

function readStored(): number | null {
  const value = localStorage.getItem(STORAGE_KEY);
  const num = value ? Number(value) : NaN;
  return Number.isFinite(num) ? num : null;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [activeOrgId, setActiveOrgIdState] = useState<number | null>(readStored());
  const [loading, setLoading] = useState(true);
  const [loadedUserId, setLoadedUserId] = useState<number | null>(null);

  // Keep the API client's org header in sync with the active org on first mount.
  useEffect(() => {
    setApiOrganizationId(activeOrgId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setActiveOrgId = useCallback((id: number | null) => {
    setApiOrganizationId(id);
    if (id == null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, String(id));
    }
    setActiveOrgIdState(id);
  }, []);

  const refreshOrgs = useCallback(async () => {
    const data = await apiJson<Organization[]>("/api/organizations");
    setOrgs(data);
    return data;
  }, []);

  useEffect(() => {
    if (!user) {
      setOrgs([]);
      setActiveOrgId(null);
      setLoadedUserId(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await refreshOrgs();
        if (cancelled) return;
        const stored = readStored();
        if (stored && data.some((o) => o.id === stored)) {
          setActiveOrgId(stored);
        } else if (data.length === 1) {
          setActiveOrgId(data[0].id);
        } else {
          // 0 orgs (create one) or multiple with no valid choice (must pick)
          setActiveOrgId(null);
        }
      } catch {
        if (!cancelled) setOrgs([]);
      } finally {
        if (!cancelled) {
          setLoadedUserId(user.id);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, refreshOrgs, setActiveOrgId]);

  const createOrg = useCallback(
    async (name: string) => {
      const org = await apiPostJson<Organization>("/api/organizations", { name });
      await refreshOrgs();
      setActiveOrgId(org.id);
      return org;
    },
    [refreshOrgs, setActiveOrgId]
  );

  const activeOrg = orgs.find((o) => o.id === activeOrgId) ?? null;
  // Auth updates before this provider's organization request starts. Treat that
  // transition as loading so route guards cannot redirect to the picker early.
  const isLoadingForUser = loading || Boolean(user && loadedUserId !== user.id);

  return (
    <OrganizationContext.Provider
      value={{
        orgs,
        activeOrg,
        activeOrgId,
        loading: isLoadingForUser,
        setActiveOrgId,
        createOrg,
        refreshOrgs,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const ctx = useContext(OrganizationContext);
  if (!ctx) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return ctx;
}
